import { PixelRatio, Platform } from 'react-native';
import type {
  CloudinaryConfiguration,
  ImageTransform,
  ImageTransformOptions,
} from 'cloudinary-tiny-js';
import cloudinary from 'cloudinary-tiny-js';

/**
 * Processes template image uri and cloudinary transform config into new cloudinary URL.
 */
export function getCloudinaryUrl(
  templateImage: string,
  transformConfig: ImageTransform | readonly ImageTransform[],
) {
  const cl = cloudinary(CLOUDINARY_CONFIG);

  if (!transformConfig) {
    return templateImage;
  }

  // we set DPR setting inline to support dynamic pixel ratio change and inline mocking
  const pixelRatioSetting = { dpr: PixelRatio.get() };

  const transformOptions = {
    transformations: Array.isArray(transformConfig)
      ? amendWithDefaultTransforms([pixelRatioSetting, ...transformConfig])
      : { ...pixelRatioSetting, ...transformConfig },
  };

  return cl(templateImage, transformOptions as ImageTransformOptions);
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * This is intended to support `imageTransformDefaults` for array configs,
 * currently library do not support configs in array
 * @see {@link https://github.com/marnusw/cloudinary-tiny-js/issues/14}
 */
function amendWithDefaultTransforms(
  transformations: readonly ImageTransform[],
): readonly ImageTransform[] {
  return [DEFAULT_TRANSFORMS, ...transformations];
}

function getImageFormat() {
  if (Platform.OS !== 'web') return 'webp';

  const canvas = document.createElement('canvas');

  // https://medium.com/@JackPu/how-to-detect-browser-support-webp-446b8f91504
  const isWebpSupported =
    canvas?.getContext?.('2d') &&
    canvas?.toDataURL?.('image/webp')?.startsWith('data:image/webp');

  if (isWebpSupported) {
    return 'webp';
  }

  return 'auto';
}

// ─── Constants ───────────────────────────────────────────────────────────────

const DEFAULT_TRANSFORMS: ImageTransform = {
  // Since native is not considered browser, Cloudinary doesn't send back Webp
  // format back. We need to explicitly tell it to send the Webp format back.
  format: getImageFormat(),
  quality: 'auto:good',
};

const CLOUDINARY_CONFIG: CloudinaryConfiguration = {
  /** The name of your Cloudinary account, a unique public identifier for URL
   * building and API access. */
  cloudName: 'sweetgreen',
  /**
   * The type of asset to deliver, `image` by default. Valid values: `image`,
   * `raw`, or `video`. Only the `image` type currently supports transforms.
   */
  assetType: 'image',
  /**
   * The storage or delivery type. Default: upload. For details on all possible
   * types, see the Cloudinary docs on delivery types.
   */
  deliveryType: 'fetch',
  /** Use https instead of http */
  secure: true,
  /** Transforms applied to all images, any transforms passed later will extend
   * these defaults. */
  imageTransformDefaults: DEFAULT_TRANSFORMS,
};
